import React, { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setState, setModalData } from "@/store/globalSlice"
import ModalCloseButton from "@/Components/ModalCloseButton"
import classNames from "classnames"
import { MODAL_SHELL_CLASS } from "@/Components/Modal"
import {
  gameBackgrounds,
  setStoredBackgroundId,
  IGameBackground,
} from "@/utils/gameBackgrounds"
import PiecePicker from "@/Components/PiecePicker"
import SoundSettings from "@/Components/SoundSettings"

type SettingsSection = "background" | "piece" | "sound"

// The settings menu is intentionally list-driven so more sections can be added
// later. `hint` is the one-liner under the label in the left rail, `description`
// the one under the title of the section itself — they say different things on
// purpose, so the rail stays scannable while the header explains the section.
const SETTINGS_SECTIONS: {
  id: SettingsSection
  label: string
  icon: string
  hint: string
  description: string
}[] = [
  {
    id: "background",
    label: "Background",
    icon: "fa-image",
    hint: "The scene behind the board",
    description: "Applies straight away and is remembered on this device.",
  },
  {
    id: "piece",
    label: "Piece",
    icon: "fa-chess-pawn",
    hint: "The token you play as",
    description: "Saved to your account and used in every game you join.",
  },
  {
    id: "sound",
    label: "Sound",
    icon: "fa-volume-high",
    hint: "Music and volumes",
    description: "Every change takes effect while you listen.",
  },
]

const SettingsModal: React.FC = () => {
  const dispatch = useAppDispatch()
  const visible = useAppSelector((state) => state.global.modalData.settings?.visible)
  const selectedBackgroundId = useAppSelector((state) => state.global.selectedTheme?.id)
  const [activeSection, setActiveSection] = useState<SettingsSection>("background")

  const handleClose = () => {
    dispatch(setModalData({ settings: { visible: false } }))
  }

  // Apply immediately so the chosen background previews live behind the modal,
  // and persist it so the choice is remembered on the next visit.
  const handleSelectBackground = (bg: IGameBackground) => {
    setStoredBackgroundId(bg.id)
    dispatch(setState({ selectedTheme: { id: bg.id, name: bg.name, image: bg.id } }))
  }

  useEffect(() => {
    if (!visible) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        handleClose()
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [visible])

  if (!visible) return null

  const active =
    SETTINGS_SECTIONS.find((s) => s.id === activeSection) ?? SETTINGS_SECTIONS[0]

  return (
    <div
      className="settings-overlay-enter fixed inset-0 z-[300] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm mobile:p-[10px]"
      onClick={handleClose}
      data-testid="game.settings-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        className={classNames(
          MODAL_SHELL_CLASS,
          // overflow-hidden matters: without it the rail's tint and border spill
          // past the panel's rounded corners.
          "settings-panel-enter relative flex h-[80vh] w-full max-w-5xl overflow-hidden sm:h-[92vh]",
          // Below 1000px index.css halves the root font size, so every rem step
          // in this panel lands at half of what it reads as on desktop:
          // `max-w-5xl` is 512px there and `rounded-2xl` an 8px corner. The px
          // steps in this file restate the ones that matter at phone size — the
          // game is landscape-locked, so "mobile" here is a ~800x400 screen.
          "mobile:max-w-[640px] mobile:rounded-[14px]"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left rail — brand, sections, and the keyboard hint pinned at the bottom. */}
        <aside className="flex w-56 shrink-0 flex-col border-r border-white/10 bg-black/40 sm:w-44 mobile:w-[132px]">
          <div className="flex items-center gap-3 px-4 pb-4 pt-5 sm:pb-3 sm:pt-4 mobile:gap-[8px] mobile:px-[10px] mobile:pb-[8px] mobile:pt-[10px]">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-500/20 sm:h-9 sm:w-9 mobile:h-[26px] mobile:w-[26px] mobile:rounded-[8px]">
              <i className="fa-solid fa-gear text-base text-black/80 mobile:text-[13px]"></i>
            </span>
            <div className="min-w-0">
              <h2 className="font-poppins text-base font-bold tracking-wide text-white mobile:text-[14px]">
                Settings
              </h2>
              <p className="truncate font-poppins text-[11px] text-white/40 mobile:text-[10px]">
                Make the game yours
              </p>
            </div>
          </div>

          <nav
            aria-label="Settings sections"
            className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 pb-3 mobile:gap-[4px] mobile:px-[8px] mobile:pb-[8px]"
          >
            {SETTINGS_SECTIONS.map((section) => {
              const isActive = activeSection === section.id
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  data-testid={`game.settings-section.${section.id}`}
                  aria-current={isActive ? "page" : undefined}
                  className={classNames(
                    "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors sm:py-2",
                    // A row is the only thing you tap in this rail, so it keeps
                    // a real target height on a phone rather than the 4px of
                    // padding `py-2` comes out as.
                    "mobile:gap-[8px] mobile:rounded-[10px] mobile:px-[9px] mobile:py-[8px]",
                    isActive
                      ? "bg-amber-500/20 text-amber-100 ring-1 ring-amber-400/40"
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  )}
                >
                  {/* Accent bar on the active row — reads as "you are here" even
                      when the amber tint is hard to see on a bright background. */}
                  <span
                    className={classNames(
                      "absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-amber-400 transition-opacity mobile:h-[16px] mobile:w-[3px]",
                      isActive ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <i
                    className={classNames(
                      "fa-solid w-5 shrink-0 text-center text-sm transition-colors mobile:w-[14px] mobile:text-[12px]",
                      section.icon,
                      isActive
                        ? "text-amber-300"
                        : "text-white/40 group-hover:text-white/70"
                    )}
                  ></i>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-poppins text-sm font-semibold mobile:text-[13px]">
                      {section.label}
                    </span>
                    {/* Dropped on short screens, where the rail needs the height
                        more than it needs the extra line. */}
                    <span
                      className={classNames(
                        "block truncate font-poppins text-[11px] sm:hidden",
                        isActive ? "text-amber-200/70" : "text-white/40"
                      )}
                    >
                      {section.hint}
                    </span>
                  </span>
                </button>
              )
            })}
          </nav>

          <div className="border-t border-white/10 px-4 py-3 sm:hidden">
            <p className="font-poppins text-[11px] text-white/30">
              <kbd className="rounded border border-white/20 bg-white/5 px-1.5 py-0.5 font-poppins text-[10px] font-semibold text-white/50">
                Esc
              </kbd>{" "}
              to close
            </p>
          </div>
        </aside>

        {/* Section content */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex shrink-0 items-center justify-between gap-4 border-b border-white/10 px-6 py-4 sm:px-4 sm:py-3 mobile:gap-[10px] mobile:px-[12px] mobile:py-[7px]">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/20 text-amber-300 ring-1 ring-amber-400/30 sm:hidden">
                <i className={classNames("fa-solid text-sm", active.icon)}></i>
              </span>
              <div className="min-w-0">
                <h3 className="truncate font-poppins text-xl font-bold text-white sm:text-lg mobile:text-[16px]">
                  {active.label}
                </h3>
                <p className="truncate font-poppins text-xs text-white/40 mobile:text-[10px]">
                  {active.description}
                </p>
              </div>
            </div>
            <ModalCloseButton onClick={handleClose} data-testid="game.settings-close" />
          </div>

          {/* Keyed on the section so switching tabs starts at the top rather than
              inheriting the previous section's scroll position. */}
          <div
            key={activeSection}
            className="settings-section-enter challenge-scroll flex-1 overflow-y-auto px-6 py-5 sm:px-4 sm:py-4 mobile:px-[12px] mobile:py-[12px]"
          >
            {activeSection === "background" && (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 mobile:grid-cols-3 mobile:gap-[10px]">
                {gameBackgrounds.map((bg) => {
                  const isSelected = bg.id === selectedBackgroundId
                  return (
                    <button
                      key={bg.id}
                      type="button"
                      onClick={() => handleSelectBackground(bg)}
                      data-testid={`game.background-option.${bg.id}`}
                      aria-pressed={isSelected}
                      title={bg.name}
                      className={classNames(
                        "group relative aspect-video overflow-hidden rounded-xl transition-shadow duration-200 mobile:rounded-[8px]",
                        isSelected
                          ? "shadow-lg shadow-amber-500/20"
                          : "hover:shadow-lg hover:shadow-black/50"
                      )}
                    >
                      <img
                        src={bg.thumbnailUrl}
                        alt={bg.name}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        style={{ contentVisibility: "auto" }}
                      />
                      {/* The frame is an overlay rather than a border on the
                          button: a border would resize the thumbnail when it
                          thickens on select, and an inset ring would sit under
                          the image. */}
                      <span
                        className={classNames(
                          "pointer-events-none absolute inset-0 rounded-xl border-2 transition-colors mobile:rounded-[8px]",
                          isSelected
                            ? "border-amber-400"
                            : "border-white/10 group-hover:border-white/40"
                        )}
                      />
                      <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent px-2 pb-1.5 pt-5 text-left font-poppins text-[11px] font-semibold text-white mobile:truncate mobile:px-[5px] mobile:pb-[3px] mobile:pt-[14px] mobile:text-[10px]">
                        {bg.name}
                      </span>
                      {isSelected ? (
                        <span className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-amber-400 text-black shadow-lg shadow-black/50 mobile:right-[4px] mobile:top-[4px] mobile:h-[16px] mobile:w-[16px]">
                          <i className="fa-solid fa-check text-xs mobile:text-[9px]"></i>
                        </span>
                      ) : (
                        // Hover-only affordance, and a phone has no hover — a
                        // tap would just leave it stuck on the thumbnail you
                        // last touched, so it doesn't ship there.
                        <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity duration-200 group-hover:opacity-100 mobile:hidden">
                          <span className="rounded-full bg-white/90 px-2.5 py-1 font-poppins text-[10px] font-bold uppercase tracking-wide text-black">
                            Use this
                          </span>
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            )}

            {activeSection === "piece" && (
              <PiecePicker testIdPrefix="game.settings" />
            )}

            {activeSection === "sound" && (
              <SoundSettings testIdPrefix="game.settings" />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SettingsModal
